/**
 * InactivityMonitor - Detects user inactivity for hint nudges.
 *
 * Direct port from bumblebee InactivityMonitor.
 * Fires callback after configurable timeout of no interaction.
 *
 * @see GitHub Issue #852
 */

const DEFAULT_TIMEOUT_MS = 120000; // 2 minutes

export default class InactivityMonitor {
    /**
     * @param {HTMLElement} container - Element to monitor for interaction
     * @param {function} onInactive - Callback when inactivity threshold reached
     * @param {number} [timeoutMs] - Inactivity threshold in ms
     */
    constructor (container, onInactive, timeoutMs) {
        this._container = container;
        this._onInactive = onInactive;
        this._timeoutMs = timeoutMs || DEFAULT_TIMEOUT_MS;
        this._timer = null;
        this._active = false;

        this._handleActivity = this._handleActivity.bind(this);
    }

    /**
     * Start monitoring for inactivity.
     */
    start () {
        if (this._active) return;
        this._active = true;
        this._container.addEventListener('pointerdown', this._handleActivity);
        this._container.addEventListener('keydown', this._handleActivity);
        this._resetTimer();
    }

    /**
     * Stop monitoring.
     */
    stop () {
        this._active = false;
        this._clearTimer();
        this._container.removeEventListener('pointerdown', this._handleActivity);
        this._container.removeEventListener('keydown', this._handleActivity);
    }

    /**
     * Reset the timer (call when user takes a meaningful action).
     */
    resetTimer () {
        if (this._active) {
            this._resetTimer();
        }
    }

    destroy () {
        this.stop();
        this._onInactive = null;
    }

    _handleActivity () {
        this._resetTimer();
    }

    _resetTimer () {
        this._clearTimer();
        this._timer = setTimeout(() => {
            if (this._onInactive) {
                this._onInactive();
            }
        }, this._timeoutMs);
    }

    _clearTimer () {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    }
}
